import { apiClient } from "@/services/api/axiosClient";
import type { ApiResponse } from "@/services/api/api.types";
import { API_ENDPOINTS } from "@/services/api/endpoints";
import axios, { type AxiosError, type AxiosRequestConfig } from "axios";
import type { LoaderType } from "@/shared/loading";

function messageFromAxiosError(error: AxiosError): string {
  const raw = error.response?.data;
  if (raw && typeof raw === "object") {
    const o = raw as Record<string, unknown>;
    const msg = o.message;
    if (typeof msg === "string" && msg.trim()) return msg;
    const err = o.error;
    if (typeof err === "string" && err.trim()) return err;
  }
  return error.message || "Unable to apply promo code.";
}

export type ApplyCouponCodePayload = {
  code: string;
  user_id: string;
  order_amount: number;
  category: string | null;
};

export type AppliedCouponBreakdown = {
  discountAmount: number;
  finalAmount?: number;
};

type CouponApplyBody = Record<string, unknown>;

/** Backend may send `success: true` or `status: "success"` (see coupon apply API). */
function isCouponApplySuccess(body: unknown): boolean {
  if (body == null || typeof body !== "object") return false;
  const o = body as Record<string, unknown>;
  if (o.success === true) return true;
  const st = o.status;
  return typeof st === "string" && st.toLowerCase() === "success";
}

function messageFromResponseBody(body: unknown): string | null {
  if (body == null || typeof body !== "object") return null;
  const m = (body as { message?: unknown }).message;
  return typeof m === "string" && m.trim() ? m : null;
}

function pickBreakdown(raw: unknown): AppliedCouponBreakdown {
  if (raw == null || typeof raw !== "object") {
    return { discountAmount: 0 };
  }
  const o = raw as CouponApplyBody;
  const nested = o.data;
  const source = nested && typeof nested === "object" ? (nested as CouponApplyBody) : o;

  const discountAmount =
    Number(
      source.discount_amount ?? source.discount ?? source.discountAmount ?? source.discount_value
    ) || 0;
  const finalRaw =
    source.final_amount ??
    source.amount_after_discount ??
    source.finalAmount ??
    source.amountAfterDiscount;
  const finalNum = finalRaw != null && finalRaw !== "" ? Number(finalRaw) : Number.NaN;
  const finalAmount = Number.isFinite(finalNum) ? finalNum : undefined;

  return { discountAmount, finalAmount };
}

export const couponService = {
  async applyCouponCode(payload: ApplyCouponCodePayload): Promise<AppliedCouponBreakdown> {
    try {
      const { data } = await apiClient.post<ApiResponse<unknown>>(
        API_ENDPOINTS.COUPON.APPLY,
        payload,
        {
          metadata: { loaderType: "inline" },
        } as AxiosRequestConfig & { metadata?: { loaderType?: LoaderType; elementId?: string } }
      );

      if (!isCouponApplySuccess(data)) {
        throw new Error(messageFromResponseBody(data) ?? "Coupon could not be applied");
      }

      return pickBreakdown(data.data);
    } catch (e) {
      if (axios.isAxiosError(e)) {
        throw new Error(messageFromAxiosError(e));
      }
      throw e;
    }
  },
};
